import React, { useState, useCallback, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { Link } from "react-router-dom";
import {
  addAttendance,
  deleteEmployee,
  getAllEmployee,
} from "../../services/services";
import { toast } from "react-toastify";
import moment from "moment";
import Loading from "../../components/loading";
import Pagination from "../../components/Pagination";
import { confirmDelete } from "../../components/confirmDelete";

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch employees
  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        page: currentPage,
        limit: itemsPerPage,
      };

      const res = await getAllEmployee(payload);

      if (res.data?.status) {
        const data = res.data.response || {};
        setEmployees(data.employees || []);
        setTotalPages(data.totalPages || 1);
        setTotalItems(data.total || 0);
      } else {
        setEmployees([]);
        toast.error("Failed to load employees");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load employees");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleDeleteEmployee = async (id, name) => {
    const confirmed = await confirmDelete(`${name}`);
    if (!confirmed) return;

    try {
      const res = await deleteEmployee(id);
      if (res.data?.status) {
        toast.success(res.data.message || "Employee deleted successfully");
        fetchEmployees();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete employee");
    }
  };
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        // fontWeight: "600",
        // fontSize: "14px",
        textTransform: "uppercase",
      },
    },
  };

  // Single function for both punch in and punch out
  const handlePunch = async (employeeId, type) => {
    // type: "in" | "out"
    const today = moment().format("YYYY-MM-DD");
    const now = new Date();

    const payload = {
      employee: employeeId,
      date: today,
      [type === "in" ? "punchIn" : "punchOut"]: now,
    };

    try {
      const res = await addAttendance(payload);
      if (res.data?.status) {
        toast.success(
          type === "in"
            ? "Punch In recorded successfully ✓"
            : "Punch Out recorded successfully ✓",
        );
      } else {
        toast.warn(res.data?.message || "Operation completed with warning");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to record attendance",
      );
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "Name",
        selector: (row) => row.name,
        sortable: true, 
        grow: 2,
      },
      {
        name: "Mobile",
        selector: (row) => row.mobile,
        sortable: true, 
      },
      {
        name: "Email",
        selector: (row) => row.email || "—", 
        grow: 3,
      },
      {
        name: "Department",
        selector: (row) => row.department || "—",
        sortable: true, 
      },
      {
        name: "Role",
        selector: (row) => row.role || "—", 
      },
      {
        name: "Join Date",
        selector: (row) =>
          row.joinDate ? moment(row.joinDate).format("DD MMM, YYYY") : "—",
        sortable: true,
        // center: true, 
      },
      {
        name: "Actions",
        // center: "true",
        // width: "280px",
        cell: (row) => (
          <div className="d-flex gap-1 justify-content-center">
            <Link to={`/employee/edit/${row._id}`}>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={(e) => e.stopPropagation()}
                title="Edit employee"
              >
                <i className="bi bi-pencil"></i>
              </button>
            </Link>

            <button
              className="btn btn-sm btn-outline-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteEmployee(row._id, row.name);
              }}
              title="Delete employee"
            >
              <i className="bi bi-trash"></i>
            </button>

            {/* <button
              className="btn btn-sm btn-success"
              onClick={(e) => {
                e.stopPropagation();
                handlePunch(row._id, "in");
              }}
              title="Punch In"
            >
              <i className="bi bi-fingerprint me-1"></i>
              In
            </button>

            <button
              className="btn btn-sm btn-warning text-white"
              onClick={(e) => {
                e.stopPropagation();
                handlePunch(row._id, "out");
              }}
              title="Punch Out"
            >
              <i className="bi bi-fingerprint me-1"></i>
              Out
            </button> */}
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div id="main">
      <div className="row">
        <div className="col-12 d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0 fw-bold">Manage Employees</h4>

          <Link to="add" className="btn btn-primary">
            <i className="bi bi-plus-lg"></i>
            <span className="d-none d-sm-inline ms-1">Add Employee</span>
          </Link>
        </div>

        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body p-0 rounded">
              <DataTable
                columns={columns}
                data={employees}
                progressPending={loading}
                customStyles={customStyles}
                progressComponent={<Loading />}
                noDataComponent={
                  <div className="py-5 text-center text-muted">
                    <i className="bi bi-people display-1 mb-3"></i>
                    <p className="fs-5 mb-0">No employees found</p>
                    <small>Click "Add Employee" to get started</small>
                  </div>
                }
                highlightOnHover
                striped
                responsive
              />
            </div>
          </div>

          {employees.length > 0 && (
            <div className="mt-3">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                limit={itemsPerPage}
                totalItems={totalItems}
                onLimitChange={(limit) => {
                  setItemsPerPage(limit);
                  setCurrentPage(1);
                }}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default headerLayoutWrapper(Employees);
